/**
 * Environment configuration management for the TherapyLink Sales Site
 *
 * This module provides type-safe access to environment variables and configuration
 * with proper validation and fallback values.
 */

/**
 * Application environment types
 */
export type Environment = 'development' | 'production';

/**
 * Configuration interface defining all application settings
 */
export interface AppConfig {
  /** Current environment */
  environment: Environment;
  /** API base URL */
  apiUrl: string;
  /** Contact form submission endpoint */
  contactFormUrl: string;
  /** Main application URL */
  appUrl: string;
  /** Contact email address */
  contactEmail: string;
  /** Sales phone number */
  salesPhone: string;
  /** Enable analytics */
  enableAnalytics: boolean;
  /** Google Analytics ID (if applicable) */
  googleAnalyticsId?: string;
  /** Enable debug logging */
  enableDebugLogging: boolean;
  /** Maximum file upload size in MB */
  maxFileUploadSize: number;
  /** Supported contact form languages */
  supportedLanguages: string[];
}

/**
 * Get the current environment from NODE_ENV or VITE_NODE_ENV
 */
function getEnvironment(): Environment {
  const env = import.meta.env.MODE || import.meta.env.NODE_ENV || 'development';

  if (env === 'production') {
    return env as Environment;
  }

  return 'development';
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string = ''): string {
  return import.meta.env[key] || fallback;
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, fallback: boolean = false): boolean {
  const value = getEnvVar(key);
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return fallback;
}

/**
 * Get numeric environment variable
 */
function getNumberEnvVar(key: string, fallback: number): number {
  const value = getEnvVar(key);
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Current environment
 */
export const environment = getEnvironment();

/**
 * Main application configuration
 */
export const config: AppConfig = {
  environment,

  // API Configuration
  apiUrl: getEnvVar(
    'VITE_API_URL',
    environment === 'production'
      ? 'https://api.therapy-link.org'
      : 'http://localhost:8000'
  ),

  // Contact Form Configuration
  contactFormUrl: getEnvVar('VITE_CONTACT_FORM_URL', '/api/v1/contact/'),

  // Application URLs
  appUrl: getEnvVar(
    'VITE_APP_URL',
    environment === 'production'
      ? 'https://dev.therapy-link.org'
      : 'http://localhost:3000'
  ),

  // Contact Information
  contactEmail: getEnvVar('VITE_CONTACT_EMAIL', 'sales@therapy-link.org'),
  salesPhone: getEnvVar('VITE_SALES_PHONE', '+1 (619) 500-3836'),

  // Analytics and Tracking
  enableAnalytics: getBooleanEnvVar(
    'VITE_ENABLE_ANALYTICS',
    environment === 'production'
  ),
  googleAnalyticsId: getEnvVar('VITE_GOOGLE_ANALYTICS_ID'),

  // Development and Debugging
  enableDebugLogging: getBooleanEnvVar(
    'VITE_ENABLE_DEBUG_LOGGING',
    environment === 'development'
  ),

  // File Upload Configuration
  maxFileUploadSize: getNumberEnvVar('VITE_MAX_FILE_UPLOAD_SIZE', 10), // MB

  // Internationalization
  supportedLanguages: getEnvVar('VITE_SUPPORTED_LANGUAGES', 'en').split(','),
};

/**
 * Validate configuration on startup
 */
function validateConfig(): void {
  const errors: string[] = [];

  // Required configuration checks
  if (!config.contactEmail.includes('@')) {
    errors.push('Invalid contact email address');
  }

  if (config.enableAnalytics && !config.googleAnalyticsId) {
    console.warn('Analytics enabled but no Google Analytics ID provided');
  }

  if (config.maxFileUploadSize <= 0) {
    errors.push('Invalid file upload size limit');
  }

  if (errors.length > 0) {
    console.error('Configuration validation errors:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }
}

// Validate configuration on module load
validateConfig();

/**
 * Utility functions for common configuration checks
 */
export const configUtils = {
  /**
   * Check if running in development mode
   */
  isDevelopment: () => environment === 'development',

  /**
   * Check if running in production mode
   */
  isProduction: () => environment === 'production',

  /**
   * Check if analytics should be enabled
   */
  shouldEnableAnalytics: () =>
    config.enableAnalytics && !!config.googleAnalyticsId,

  /**
   * Get the full contact form submission URL
   */
  getContactFormSubmissionUrl: () => {
    if (config.contactFormUrl.startsWith('http')) {
      return config.contactFormUrl;
    }
    return `${config.apiUrl}${config.contactFormUrl}`;
  },

  /**
   * Log debug information (only in development)
   */
  debugLog: (message: string, ...args: unknown[]) => {
    if (config.enableDebugLogging) {
      console.debug(`[TherapyLink Sales]`, message, ...args);
    }
  },

  /**
   * Get configuration for display (excluding sensitive data)
   */
  getDisplayConfig: () => ({
    environment: config.environment,
    apiUrl: config.apiUrl,
    appUrl: config.appUrl,
    enableAnalytics: config.enableAnalytics,
    enableDebugLogging: config.enableDebugLogging,
    maxFileUploadSize: config.maxFileUploadSize,
    supportedLanguages: config.supportedLanguages,
  }),
};

// Log configuration in development
if (configUtils.isDevelopment()) {
  console.info(
    'TherapyLink Sales Site Configuration:',
    configUtils.getDisplayConfig()
  );
}

export default config;
